import { request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

type CurrencyListApiRow = {
  code?: string
  icon?: string
  name?: string
  pair?: string
  price?: string
  change?: string
  changeType?: number
  volume24h?: string
  marketDepth?: string
  liquidity?: string
}

type CurrencyListApiResponse = {
  title?: string
  coinCount?: string
  pairCount?: string
  liquidityRank?: string
  list?: CurrencyListApiRow[] | null
  total?: number | string
  page?: number | string
  size?: number | string
  totalPages?: number | string
}

export type OfficialTokenCurrencyListParams = {
  exchangeId: string
  page?: number
  size?: number
}

export type OfficialTokenCurrencyListRow = {
  code?: string
  icon?: string
  name?: string
  pair?: string
  price?: string
  change?: string
  changeType?: number
  volume24h?: string
  marketDepth?: string
  liquidity?: string
}

export type OfficialTokenCurrencyListData = {
  title?: string
  coinCount?: string
  pairCount?: string
  liquidityRank?: string
  list: OfficialTokenCurrencyListRow[]
  total: number
  page: number
  size: number
  totalPages: number
}

const DEFAULT_PAGE_SIZE = 20

const isObject = (value: unknown): value is Record<string, any> => Boolean(value && typeof value === 'object')

const toArray = (value: unknown): OfficialTokenCurrencyListRow[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter(isObject)
}

const toNumber = (value: unknown, fallback = 0) => {
  const num = Number(value)

  return Number.isFinite(num) ? num : fallback
}

const pickPayload = (response: unknown): CurrencyListApiResponse | null => {
  if (!isObject(response)) {
    return null
  }

  if (isObject(response.data)) {
    return response.data as CurrencyListApiResponse
  }

  return response as CurrencyListApiResponse
}

const normalizeCurrencyList = (
  response: unknown,
  fallbackParams: Required<Pick<OfficialTokenCurrencyListParams, 'page' | 'size'>>,
): OfficialTokenCurrencyListData | null => {
  const payload = pickPayload(response)

  if (!payload) {
    return null
  }

  const list = toArray(payload.list)
  const size = toNumber(payload.size, fallbackParams.size) || fallbackParams.size
  const total = toNumber(payload.total, list.length)
  const totalPages = toNumber(payload.totalPages, total > 0 ? Math.ceil(total / size) : 1)

  return {
    title: payload.title,
    coinCount: payload.coinCount,
    pairCount: payload.pairCount,
    liquidityRank: payload.liquidityRank,
    list,
    total,
    page: toNumber(payload.page, fallbackParams.page) || fallbackParams.page,
    size,
    totalPages: totalPages > 0 ? totalPages : 1,
  }
}

export const getOfficialTokenCurrencyList = async (
  params: OfficialTokenCurrencyListParams,
): Promise<OfficialTokenCurrencyListData | null> => {
  const api = useEnvApi()
  const requestParams = {
    exchangeId: params.exchangeId,
    page: params.page ?? 1,
    size: params.size ?? DEFAULT_PAGE_SIZE,
  }
  const requestConfig = {
    baseURL: api.wikibitBffGo,
    url: '/api/v4/bit/web/currency/list',
    method: 'GET' as const,
    params: requestParams,
  }

  if (import.meta.server) {
    return normalizeCurrencyList(
      await useServerFetch<CurrencyListApiResponse>({
        ...requestConfig,
        query: requestParams,
      }),
      requestParams,
    )
  }

  return normalizeCurrencyList(await request<unknown, CurrencyListApiResponse>(requestConfig), requestParams)
}
